import React, { useState } from 'react';
import { FileText, Download, Eye, Book, Calendar, User, Filter } from 'lucide-react';

const DesignBasisDocuments = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const documents = [
    {
      id: 1,
      name: 'Process Design Basis',
      category: 'Design Basis',
      documentNumber: 'DB-001-2024',
      revision: 'Rev. 2',
      author: 'John Smith',
      department: 'Process Engineering',
      lastUpdated: '2025-11-10',
      status: 'Approved',
      fileSize: '5.2 MB',
      description: 'Complete process design basis including feed specifications, product requirements, and design philosophy'
    },
    {
      id: 2,
      name: 'Equipment Design Specifications',
      category: 'Engineering',
      documentNumber: 'ENG-002-2024',
      revision: 'Rev. 1',
      author: 'Sarah Johnson',
      department: 'Mechanical Engineering',
      lastUpdated: '2025-10-25',
      status: 'Approved',
      fileSize: '3.8 MB',
      description: 'Mechanical design specifications for all major equipment including vessels, heat exchangers, and rotating equipment'
    },
    {
      id: 3,
      name: 'Process Safety Design Requirements',
      category: 'Safety',
      documentNumber: 'SAF-003-2024',
      revision: 'Rev. 3',
      author: 'Michael Chen',
      department: 'Process Safety',
      lastUpdated: '2025-12-01',
      status: 'Under Review',
      fileSize: '4.1 MB',
      description: 'Safety design requirements including pressure relief, emergency shutdown systems, and safety instrumented systems'
    },
    {
      id: 4,
      name: 'Piping Design Criteria',
      category: 'Engineering',
      documentNumber: 'ENG-004-2024',
      revision: 'Rev. 2',
      author: 'Emily Davis',
      department: 'Piping Engineering',
      lastUpdated: '2025-09-15',
      status: 'Approved',
      fileSize: '2.9 MB',
      description: 'Piping design criteria including material selection, stress analysis requirements, and layout guidelines'
    },
    {
      id: 5,
      name: 'Utilities Design Basis',
      category: 'Design Basis',
      documentNumber: 'DB-005-2024',
      revision: 'Rev. 1',
      author: 'Robert Taylor',
      department: 'Utilities Engineering',
      lastUpdated: '2025-11-20',
      status: 'Approved',
      fileSize: '3.5 MB',
      description: 'Design basis for all utility systems including steam, cooling water, compressed air, and nitrogen'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Documents', count: documents?.length },
    { id: 'design-basis', name: 'Design Basis', count: 2 },
    { id: 'engineering', name: 'Engineering', count: 2 },
    { id: 'safety', name: 'Safety', count: 1 }
  ];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{documents?.length}</p>
            </div>
            <Book className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">4</p>
            </div>
            <FileText className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Under Review</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">1</p>
            </div>
            <Calendar className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Contributors</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">5</p>
            </div>
            <User className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category?.id
                    ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category?.name} ({category?.count})
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
              <Filter className="w-5 h-5 text-gray-600" />
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
              Add Document
            </button>
          </div>
        </div>
      </div>
      {/* Documents List */}
      <div className="space-y-4">
        {documents?.map((doc) => (
          <div key={doc?.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{doc?.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{doc?.documentNumber} - {doc?.revision}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        doc?.status === 'Approved' ?'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800'
                      }`}>
                        {doc?.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-4">{doc?.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Category</p>
                        <p className="text-sm font-medium text-gray-900">{doc?.category}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Author</p>
                        <p className="text-sm font-medium text-gray-900">{doc?.author}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Department</p>
                        <p className="text-sm font-medium text-gray-900">{doc?.department}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">File Size</p>
                        <p className="text-sm font-medium text-gray-900">{doc?.fileSize}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">Last updated: {doc?.lastUpdated}</p>
                <div className="flex items-center space-x-2">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center space-x-2">
                    <Eye className="w-4 h-4" />
                    <span>View</span>
                  </button>
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DesignBasisDocuments;